import axiosInstance from '../../../config/axios';

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: any) {
      const isServerError = error?.response?.status >= 500;
      const shouldRetry = isServerError && attempt < maxRetries;

      if (!shouldRetry) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, 8000)));
    }
  }

  throw new Error('Failed after all retries');
}

export interface BundleItem {
  kind: 'COVER' | 'MAIN' | 'ANNEX';
  order: number;
  document_id: number;
  title: string;
  pdf_url?: string | null;
}

export interface BundleMerge {
  success: boolean;
  warnings: string[];
}

export interface BundleDetail {
  id: number;
  status: 'READY' | 'DRAFT' | 'SENT' | 'SIGNED' | 'ARCHIVED';
  employee_id: number;
  main_document_id: number;
  main_document_title: string;
  attachments_count: number;
  items: BundleItem[];
  combined_pdf_url?: string | null;
  suggested_file_name?: string;
  merge: BundleMerge;
}

export interface BundleListItem {
  id: number;
  document_id: number;
  document_name?: string;
  created_at: string;
  main_document_name?: string;
  annex_count: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  combined_pdf_url?: string;
  items: BundleItem[];
}

export interface RebuildPayload {
  order_override?: number[];
  make_cover_page: boolean;
  make_combined_pdf: boolean;
  idempotency_key?: string;
}

export async function fetchEmployeeBundles(employeeId: number): Promise<BundleListItem[]> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.get<BundleListItem[]>(
      `/api/admin/employees/${employeeId}/bundles/`
    );
    return data;
  });
}

export async function fetchBundle(bundleId: number): Promise<BundleDetail> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.get<BundleDetail>(
      `/api/admin/document-bundles/${bundleId}/`
    );
    return data;
  });
}

export async function rebuildBundle(
  bundleId: number,
  payload: RebuildPayload
): Promise<BundleDetail> {
  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.post<BundleDetail>(
      `/api/admin/document-bundles/${bundleId}/rebuild/`,
      payload
    );
    return data;
  });
}
